/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/features/attendanceModern/screens/ClockInSuccessScreen.tsx
 * PURPOSE: SC-017-EMP-ATTENDANCE [5] clock_in_success — màn xác nhận thành
 *          công riêng (✓ + giờ vào + chi nhánh + "Xong"). Trước đây web
 *          KHÔNG có màn này — chấm công xong chuyển thẳng sang card "Đang
 *          làm việc", không có bước dừng lại xác nhận.
 *          2026-09-04 (structural layout parity, root cause #1): removed the
 *          `.card` wrapper (mobile's renderClockInSuccess is a bare
 *          fragment) and combined the time+branch lines into one
 *          ("{time} · {branchName}") to match mobile's single EmptyState
 *          subtitle (`${time} · ${branchName}`), instead of 2 separate `<p>`
 *          elements.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-02
 * COMPLIANCE: SDLC 6.5.0 + WIREFRAME-V0-MASTER-REGISTRY.md §SC-017-EMP-ATTENDANCE [5]
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md §SC-017-EMP-ATTENDANCE
 * - Mobile source of truth: mobile/src/screens/attendance/AttendanceScreen.tsx renderClockInSuccess (:910-924)
 *  */

import React from 'react';
import styles from '@/pages/AttendanceModern.module.css';

interface ClockInSuccessScreenProps {
  time: string;
  branchName: string | null;
  /** BS-300 §1bis-G1 + BS-301 Route 2 §1 (2026-08-17) — backend's authoritative
   *  post-submission is_within_geofence=false result. Non-blocking (record đã
   *  lưu thành công), chỉ là banner thông báo bản ghi bị đánh dấu GPS lệch. */
  gpsDeviationWarning?: { distanceMeters: number | null } | null;
  onDone: () => void;
  /** SC-017-EMP-ATTENDANCE [8] (2026-09-02) — "Xem checklist ca (tuỳ chọn)". */
  onViewChecklist: () => void;
}

export const ClockInSuccessScreen: React.FC<ClockInSuccessScreenProps> = ({ time, branchName, gpsDeviationWarning, onDone, onViewChecklist }) => {
  return (
    <>
      <div style={{ textAlign: 'center' }} data-testid="clock-in-success-screen">
        <p style={{ fontSize: '2.5rem', margin: 0 }}>✓</p>
        <h2 className={styles.cardTitle}>Đã vào ca</h2>
        <p className={styles.cardDescription}>
          {time}
          {branchName ? ` · ${branchName}` : ''}
        </p>
      </div>
      {gpsDeviationWarning && (
        <p role="status" className={styles.timingWarningBanner} data-testid="gps-deviation-banner">
          ⚠️ Bản ghi chấm công đã được đánh dấu "GPS lệch"
          {typeof gpsDeviationWarning.distanceMeters === 'number'
            ? ` (cách chi nhánh ~${Math.round(gpsDeviationWarning.distanceMeters)}m).`
            : '.'}
        </p>
      )}
      <div className={styles.actionsRow}>
        <button type="button" className={styles.primaryButton} onClick={onDone} data-testid="clock-in-success-done">
          Xong
        </button>
        <button type="button" className={styles.secondaryButton} onClick={onViewChecklist} data-testid="clock-in-success-view-checklist">
          Xem checklist ca (tuỳ chọn) ►
        </button>
      </div>
    </>
  );
};

export default ClockInSuccessScreen;
